import React from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Popover,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';

const MainLayout = () => {
  const { notifications, userProfile } = useApp();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setNotificationAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar sx={{ justifyContent: 'flex-end', gap: 4, pr: 6, py: 1 }}>
            {/* Notifications */}
            <Box
              sx={{
                position: 'relative',
                '&:hover': {
                  '& .MuiIconButton-root': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  }
                }
              }}
            >
              <IconButton 
                onClick={handleNotificationClick}
                sx={{ 
                  width: 48,
                  height: 48,
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  transition: 'all 0.2s ease',
                }}
              >
                <Badge 
                  badgeContent={notifications.length} 
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#ff4444',
                      color: '#fff',
                      minWidth: 20,
                      height: 20,
                      fontSize: '0.75rem',
                      padding: '0 6px',
                    }
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 24, color: '#1976d2' }} />
                </Badge>
              </IconButton>
            </Box>

            {/* User Controls */}
            <Box
              onClick={handleProfileClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
                userSelect: 'none',
                padding: '8px 16px',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  '& .settings-icon': {
                    transform: 'rotate(30deg)',
                  }
                }
              }}
            >
              <Typography 
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: 500,
                  color: '#1976d2',
                  letterSpacing: '0.2px'
                }}
              >
                {userProfile.name}
              </Typography>
              <SettingsIcon 
                className="settings-icon"
                sx={{ 
                  fontSize: 22,
                  color: '#1976d2',
                  opacity: 0.8,
                  transition: 'transform 0.3s ease',
                }}
              />
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content Area */}
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            backgroundColor: '#f5f5f5',
            minHeight: 'calc(100vh - 64px)' 
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* 通知菜单 */}
      <Popover
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: { 
            width: 360,
            maxHeight: 480,
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          p: 2.5, 
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          backgroundColor: '#f8f9fa'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1565c0' }}>
            Notifications
          </Typography>
        </Box>
        <List sx={{ p: 0 }}>
          {notifications.map((notification, index) => (
            <ListItem 
              key={index}
              sx={{ 
                p: 2.5,
                borderBottom: '1px solid rgba(0,0,0,0.04)',
                bgcolor: notification.unread ? 'rgba(25, 118, 210, 0.02)' : 'transparent',
                '&:hover': { 
                  bgcolor: 'rgba(0, 0, 0, 0.02)',
                  cursor: 'pointer'
                }
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {notification.message}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {notification.time}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Popover>

      {/* 用户菜单 */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 200,
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <MenuItem sx={{ py: 1.5 }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" sx={{ color: '#1976d2' }} />
          </ListItemIcon>
          <Typography variant="subtitle2">Profile</Typography>
        </MenuItem>
        <MenuItem sx={{ py: 1.5 }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" sx={{ color: '#1976d2' }} />
          </ListItemIcon>
          <Typography variant="subtitle2">Settings</Typography>
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem sx={{ py: 1.5, color: '#d32f2f' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: '#d32f2f' }} />
          </ListItemIcon>
          <Typography variant="subtitle2">Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MainLayout; 